import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { db } from '../firebase/config';
import { collection, query, where, getDocs, updateDoc, doc, arrayUnion } from 'firebase/firestore';
import { Key, Loader2, CheckCircle, AlertCircle, ArrowLeft, Users } from 'lucide-react';
import { formatJoinCode, isValidJoinCode } from '../utils/joinCode';

export function JoinHousehold() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [joinCode, setJoinCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [householdName, setHouseholdName] = useState('');

  async function handleJoinHousehold(e: React.FormEvent) {
    e.preventDefault();

    if (!user) {
      setError('You must be logged in to join a household');
      return;
    }

    const formattedCode = formatJoinCode(joinCode);

    if (!isValidJoinCode(formattedCode)) {
      setError('Invalid code format. Use format: ABC-123');
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Find household with this join code
      const householdsRef = collection(db, 'households');
      const q = query(householdsRef, where('joinCode', '==', formattedCode));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        setError('No household found with this code. Please check and try again.');
        setLoading(false);
        return;
      }

      const householdDoc = querySnapshot.docs[0];
      const householdData = householdDoc.data();
      const householdId = householdDoc.id;

      // Check if user is already a member
      if (householdData.members?.includes(user.uid)) {
        setError('You are already a member of this household!');
        setLoading(false);
        return;
      }

      setHouseholdName(householdData.name || 'Unknown Household');

      // Add user to household members
      await updateDoc(doc(db, 'households', householdId), {
        members: arrayUnion(user.uid)
      });

      // Update user's household reference
      await updateDoc(doc(db, 'users', user.uid), {
        householdId: householdId
      });

      setSuccess(true);
      setLoading(false);

      // Redirect to dashboard after 2 seconds
      setTimeout(() => {
        navigate('/dashboard', { replace: true });
      }, 2000);

    } catch (err: any) {
      console.error('Error joining household:', err);
      setError('Failed to join household. Please try again.');
      setLoading(false);
    }
  }

  function handleCodeChange(e: React.ChangeEvent<HTMLInputElement>) {
    const formatted = formatJoinCode(e.target.value);
    setJoinCode(formatted);
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-mint/30 via-neutral-bg to-primary-mint/20 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center border border-gray-100">
          <div className="w-16 h-16 bg-yellow-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-yellow-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">Login Required</h2>
          <p className="text-gray-600 mb-6">
            You need to be logged in to join a household.
          </p>
          <div className="flex gap-3">
            <Link
              to="/login"
              className="flex-1 bg-primary-green text-white px-6 py-3 rounded-xl font-semibold hover:bg-primary-green/90 transition-all"
            >
              Log In
            </Link>
            <Link
              to="/signup"
              className="flex-1 bg-gray-100 text-gray-700 px-6 py-3 rounded-xl font-semibold hover:bg-gray-200 transition-all"
            >
              Sign Up
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-mint/30 via-neutral-bg to-primary-mint/20 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center border border-gray-100">
          <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4 animate-bounce">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">Success!</h2>
          <p className="text-gray-600 mb-2">
            You've joined <span className="font-semibold text-primary-green">{householdName}</span>
          </p>
          <p className="text-sm text-gray-500 mb-6">
            Redirecting to dashboard...
          </p>
          <div className="w-8 h-8 border-4 border-primary-green border-t-transparent rounded-full animate-spin mx-auto"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-mint/30 via-neutral-bg to-primary-mint/20 p-4 sm:p-6">
      <div className="max-w-2xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-primary-green transition-colors mb-6 group"
        >
          <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
          <span className="font-medium">Back</span>
        </button>

        <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 border border-gray-100">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-primary-green to-primary-mint rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
              <Key className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Join a Household</h1>
            <p className="text-gray-600">Enter the join code shared by your household owner</p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-4 mb-6 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <p className="text-sm">{error}</p>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleJoinHousehold} className="space-y-6">
            <div>
              <label htmlFor="joinCode" className="block text-sm font-semibold text-gray-700 mb-2">
                Household Join Code
              </label>
              <input
                type="text"
                id="joinCode"
                value={joinCode}
                onChange={handleCodeChange}
                placeholder="ABC-123"
                maxLength={7}
                required
                className="w-full px-4 py-4 text-2xl font-bold font-mono text-center border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-green focus:border-primary-green transition-all uppercase tracking-wider"
                disabled={loading}
              />
              <p className="text-xs text-gray-500 mt-2 text-center">
                Format: 3 letters, dash, 3 numbers (e.g., ABC-123)
              </p>
            </div>

            <button
              type="submit"
              disabled={loading || joinCode.length < 6}
              className="w-full bg-primary-green text-white px-6 py-4 rounded-xl font-semibold hover:bg-primary-green/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Joining...
                </>
              ) : (
                <>
                  <Users className="w-5 h-5" />
                  Join Household
                </>
              )}
            </button>
          </form>

          {/* Info Box */}
          <div className="mt-8 bg-blue-50 border border-blue-200 rounded-xl p-4">
            <p className="text-sm text-blue-900 font-semibold mb-2">Where to find a join code:</p>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Ask your household owner to check Household Settings</li>
              <li>• The code is displayed at the top in a green box</li>
              <li>• Codes are unique to each household</li>
            </ul>
          </div>

          {/* Alternative */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Have an email invitation?{' '}
              <Link to="/household-settings" className="text-primary-green font-semibold hover:underline">
                Check your email
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
